// ============================================================================
// ENGLISH TRANSLATIONS - CHAPTER 4: CRUCIFIXION AND RESURRECTION
// ============================================================================
// Levels 40-52 (13 levels, 39 questions)

export const chapter4Translations = {
  name: "Crucifixion and Resurrection",
  description: "He is risen!",
  
  spiritualWisdom: {
    principle: "Through his death and resurrection, Jesus gives us eternal life",
    scripture: "I am the resurrection and the life. The one who believes in me will live, even though they die (John 11:25)",
    application: "The cross and empty tomb are the heart of our faith and source of our hope"
  },
  
  levels: {
    40: {
      name: "The Arrest",
      challenge: "Judas, are you betraying the Son of Man with a kiss?",
      easy: {
        question: "Who betrayed Jesus?",
        options: ["Peter", "Judas", "Thomas", "Matthew"],
        hint: "One of the twelve disciples",
        funFact: "💔 Judas sold Jesus for 30 pieces of silver - the price of a slave!"
      },
      medium: {
        question: "What sign did Judas use to identify Jesus?",
        options: [
          "He pointed at him",
          "He called him by name",
          "He gave him a kiss",
          "He pointed him out to soldiers"
        ],
        hint: "A gesture of affection perverted",
        funFact: "😢 The kiss, symbol of love, becomes instrument of betrayal!"
      },
      hard: {
        question: "How did Jesus react to betrayal and arrest?",
        options: [
          "He defended himself violently",
          "He fled",
          "He accepted with dignity and healed the servant's ear",
          "He cursed his enemies"
        ],
        hint: "He showed love even to his enemies",
        funFact: "✨ Even while being arrested, Jesus healed Malchus' severed ear!"
      }
    },
    
    41: {
      name: "Peter's Denial",
      challenge: "Before the rooster crows, you will deny me three times...",
      easy: {
        question: "How many times did Peter deny Jesus?",
        options: ["1 time", "2 times", "3 times", "4 times"],
        hint: "The number Jesus had predicted",
        funFact: "🐓 The rooster's crow reminded Peter of Jesus' prophecy!"
      },
      medium: {
        question: "Where was Peter when he denied Jesus?",
        options: [
          "In the Temple",
          "At Pilate's",
          "In the courtyard of the high priest",
          "On the road"
        ],
        hint: "He was following Jesus from afar",
        funFact: "� Peter was warming himself by the fire with Jesus' enemies!"
      },
      hard: {
        question: "What does Peter's restoration after his denial teach us?",
        options: [
          "Some sins are unforgivable", 
          "Jesus' love lifts us even from our greatest falls",
          "Peter was unworthy",
          "We must hide our faults"
        ],
        hint: "It's about grace that restores",
        funFact: "� Jesus asks 'Do you love me?' 3 times to restore the 3 denials!"
      }
    },

    42: {
      name: "The Trial before Pilate",
      challenge: "Are you the King of the Jews?",
      easy: {
        question: "Who was the Roman governor who judged Jesus?",
        options: ["Caesar", "Pilate", "Herod", "Caiaphas"],
        hint: "The Roman authority in Judea",
        funFact: "⚖️ Pilate found no fault in Jesus but condemned him anyway!"
      },
      medium: {
        question: "What did Pilate ask the crowd to choose?",
        options: [
          "Between Jesus and Caesar",
          "Between Jesus and Barabbas",
          "Between life and death for Jesus",
          "Between crucifixion and stoning"
        ],
        hint: "Between Jesus and a notorious criminal",
        funFact: "🔗 Barabbas was a murderer and rebel released instead of Jesus!"
      },
      hard: {
        question: "What does Pilate's decision reveal about moral compromise?",
        options: [
          "Sometimes we must please crowds",
          "Knowing what's right but choosing convenience leads to injustice",
          "Political pressure is unavoidable",
          "Truth is relative"
        ],
        hint: "It's about choosing between truth and popularity",
        funFact: "💧 Pilate washed his hands but couldn't wash away his responsibility!"
      }
    },

    43: {
      name: "The Way of the Cross",
      challenge: "They took Jesus and led him away, carrying his cross...",
      easy: {
        question: "Who helped Jesus carry his cross?",
        options: ["Peter", "John", "Simon of Cyrene", "A Roman soldier"],
        hint: "A man from Cyrene forced to help",
        funFact: "🤝 Simon's sons became known Christians - the cross changed his family!"
      },
      medium: {
        question: "What did Jesus say to the women who wept for him?",
        options: [
          "Thank you for your compassion",
          "Don't weep for me, weep for yourselves and your children",
          "Your tears comfort me",
          "Pray for my enemies"
        ],
        hint: "He was concerned about their future, not his suffering",
        funFact: "😢 Even in agony, Jesus thinks of others' future sufferings!"
      },
      hard: {
        question: "What does Jesus carrying the cross symbolize for us?",
        options: [
          "Life is suffering",
          "Following Christ means taking up our cross daily",
          "We must suffer to be saved",
          "Physical pain brings salvation"
        ],
        hint: "It's about our daily commitment to follow Christ",
        funFact: "✝️ 'Take up your cross daily and follow me' - daily surrender!"
      }
    },

    44: {
      name: "The Crucifixion",
      challenge: "Father, forgive them, for they know not what they do...",
      easy: {
        question: "Between whom was Jesus crucified?",
        options: [
          "Two disciples",
          "Two thieves",
          "Two Romans",
          "Two Pharisees"
        ],
        hint: "Two criminals on crosses",
        funFact: "⚖️ Jesus fulfilled Isaiah's prophecy: 'numbered with transgressors'!"
      },
      medium: {
        question: "What were Jesus' first words on the cross?",
        options: [
          "My God, why have you forsaken me?",
          "Father, forgive them, for they know not what they do",
          "It is finished",
          "Into your hands I commit my spirit"
        ],
        hint: "A prayer for his executioners",
        funFact: "🙏 Jesus' first word is forgiveness - ultimate expression of love!"
      },
      hard: {
        question: "What does Jesus' prayer for forgiveness reveal about divine love?",
        options: [
          "God excuses all sins",
          "Love forgives even the unforgivable and offers grace to all",
          "Ignorance eliminates guilt",
          "Suffering makes us holy"
        ],
        hint: "It's about unconditional divine grace",
        funFact: "❤️ Even his murderers receive forgiveness - infinite divine mercy!"
      }
    },

    45: {
      name: "The Seven Last Words",
      challenge: "My God, my God, why have you forsaken me?",
      easy: {
        question: "How many words did Jesus speak from the cross?",
        options: ["Five", "Six", "Seven", "Eight"],
        hint: "The number of completion in the Bible",
        funFact: "7️⃣ Seven words revealing Jesus' heart in his greatest suffering!"
      },
      medium: {
        question: "To whom did Jesus entrust his mother?",
        options: [
          "To Peter",
          "To the beloved disciple John",
          "To Mary Magdalene",
          "To his brothers"
        ],
        hint: "The disciple whom Jesus loved",
        funFact: "👥 Jesus creates a new family: 'Woman, behold your son'!"
      },
      hard: {
        question: "What do Jesus' last words reveal about his mission?",
        options: [
          "He was defeated",
          "He accomplished salvation perfectly: 'It is finished'",
          "He was confused",
          "He gave up"
        ],
        hint: "It's about mission accomplished, not failure",
        funFact: "✅ 'It is finished' - salvation completed, debt paid in full!"
      }
    },

    46: {
      name: "The Death of Jesus",
      challenge: "Jesus cried out with a loud voice and breathed his last...",
      easy: {
        question: "What happened to the temple veil when Jesus died?",
        options: [
          "Nothing",
          "It was torn from top to bottom",
          "It caught fire",
          "It fell down"
        ],
        hint: "A supernatural tearing",
        funFact: "📜 The torn veil opened access to God - no more barrier!"
      },
      medium: {
        question: "What did the centurion say when Jesus died?",
        options: [
          "He was a good man",
          "Truly this was the Son of God",
          "He was innocent",
          "What a tragedy"
        ],
        hint: "A declaration of divinity",
        funFact: "🛡️ A Roman soldier recognized what religious leaders missed!"
      },
      hard: {
        question: "What does the torn temple veil symbolize?",
        options: [
          "The temple's destruction",
          "Direct access to God through Jesus' sacrifice",
          "The end of Judaism",
          "God's anger"
        ],
        hint: "It's about removing barriers between God and humanity",
        funFact: "🚪 Jesus' death opened the way into God's presence for all!"
      }
    },

    47: {
      name: "The Burial",
      challenge: "Joseph took the body and wrapped it in clean linen...",
      easy: {
        question: "Who asked Pilate for Jesus' body?",
        options: [
          "Mary Magdalene",
          "Joseph of Arimathea",
          "Nicodemus",
          "John"
        ],
        hint: "A secret disciple who became bold",
        funFact: "🦁 Joseph's faith emerged in Jesus' darkest hour!"
      },
      medium: {
        question: "Who helped Joseph prepare Jesus' body?",
        options: [
          "The disciples",
          "Nicodemus",
          "Mary Magdalene",
          "Roman soldiers"
        ],
        hint: "The Pharisee who came to Jesus by night",
        funFact: "🌙 Nicodemus, who once came by night, now serves in daylight!"
      },
      hard: {
        question: "What does the careful burial show about their faith?",
        options: [
          "They had given up hope",
          "They honored Jesus despite not fully understanding the resurrection",
          "They were just being practical",
          "They wanted to hide the body"
        ],
        hint: "It's about love and honor even in apparent defeat",
        funFact: "💐 Love honors Jesus even when hope seems dead!"
      }
    },

    48: {
      name: "The Empty Tomb",
      challenge: "He is not here, for he has risen as he said...",
      easy: {
        question: "Who first discovered the empty tomb?",
        options: [
          "Peter and John",
          "Mary Magdalene and other women",
          "Roman guards",
          "Joseph of Arimathea"
        ],
        hint: "Women who came to anoint the body",
        funFact: "👩 Women were first witnesses of the greatest event in history!"
      },
      medium: {
        question: "What did the angels say to the women?",
        options: [
          "Don't be afraid",
          "He is not here, he has risen",
          "Come and see",
          "All of the above"
        ],
        hint: "Multiple messages of hope and instruction",
        funFact: "👼 Angels announce the victory that changes everything!"
      },
      hard: {
        question: "What does the empty tomb prove about Jesus?",
        options: [
          "His body was stolen",
          "He conquered death and validates all his claims",
          "He never really died",
          "It's just symbolism"
        ],
        hint: "It's the ultimate validation of who Jesus is",
        funFact: "🏆 Empty tomb = Death defeated, eternal life available!"
      }
    },

    49: {
      name: "The Road to Emmaus",
      challenge: "Were not our hearts burning within us as he talked with us?",
      easy: {
        question: "How many disciples walked to Emmaus?",
        options: ["One", "Two", "Three", "Four"],
        hint: "Cleopas and another disciple",
        funFact: "🚶‍♂️🚶‍♂️ Two discouraged disciples about to have their lives changed!"
      },
      medium: {
        question: "When did they recognize Jesus?",
        options: [
          "When he spoke",
          "When he walked with them",
          "When he broke bread",
          "When he explained Scripture"
        ],
        hint: "At a familiar gesture during a meal",
        funFact: "🍞 Jesus revealed in the breaking of bread - communion significance!"
      },
      hard: {
        question: "What does this story teach about encountering Jesus?",
        options: [
          "Jesus only appears in churches",
          "Jesus meets us in our disappointment and opens our understanding",
          "Only apostles can see Jesus",
          "Jesus appears only to the faithful"
        ],
        hint: "It's about Jesus meeting us where we are, even in doubt",
        funFact: "❤️‍🔥 Hearts burning within us - sign of Jesus' presence!"
      }
    },

    50: {
      name: "Jesus Appears to the Disciples",
      challenge: "Peace be with you! As the Father sent me, so I send you...",
      easy: {
        question: "What did Jesus show the disciples to prove his identity?",
        options: [
          "His face",
          "His hands and side",
          "His clothes",
          "His voice"
        ],
        hint: "The wounds of crucifixion",
        funFact: "🔍 Scars of love become proof of victory over death!"
      },
      medium: {
        question: "What did Jesus breathe on the disciples?",
        options: [
          "Peace",
          "The Holy Spirit",
          "Life",
          "Power"
        ],
        hint: "The third person of the Trinity",
        funFact: "💨 Jesus breathes new life into his followers - new creation!"
      },
      hard: {
        question: "What commission does Jesus give his disciples?",
        options: [
          "Build churches",
          "As the Father sent me, so I send you",
          "Study Scripture",
          "Wait in Jerusalem"
        ],
        hint: "It's about continuing Jesus' mission",
        funFact: "📤 Every Christian receives this same commission today!"
      }
    },

    51: {
      name: "Doubting Thomas",
      challenge: "My Lord and my God!",
      easy: {
        question: "Which disciple was absent when Jesus first appeared?",
        options: ["Peter", "Thomas", "Andrew", "Philip"],
        hint: "The one who doubted",
        funFact: "❓ Thomas earned the nickname 'Doubting Thomas' for this moment!"
      },
      medium: {
        question: "What did Thomas demand to believe?",
        options: [
          "To hear Jesus speak",
          "To touch Jesus' wounds",
          "To see Jesus eat",
          "To see Jesus walk"
        ],
        hint: "Physical evidence of the crucifixion wounds",
        funFact: "👆 Thomas wanted tangible proof - Jesus graciously provided it!"
      },
      hard: {
        question: "What does Jesus say about those who believe without seeing?",
        options: [
          "They are naive",
          "Blessed are those who believe without seeing",
          "They need more faith",
          "They are wise"
        ],
        hint: "It's about blessing on faith without physical proof",
        funFact: "🌟 This blessing applies to all believers after the apostles!"
      }
    },

    52: {
      name: "The Great Commission",
      challenge: "Go therefore and make disciples of all nations...",
      easy: {
        question: "What are disciples to do in all nations?",
        options: [
          "Build churches",
          "Make disciples",
          "Establish governments",
          "Learn languages"
        ],
        hint: "Create more followers of Jesus",
        funFact: "🌍 The Gospel is for every people group on earth!"
      },
      medium: {
        question: "In whose name should people be baptized?",
        options: [
          "Jesus only",
          "The Father, Son, and Holy Spirit",
          "The Church",
          "The apostles"
        ],
        hint: "The Trinity - three persons, one God",
        funFact: "☁️ Baptism in the Trinity's name - full divine involvement!"
      },
      hard: {
        question: "What promise accompanies this commission?",
        options: [
          "Success guaranteed",
          "I am with you always, to the end of the age",
          "No persecution",
          "Easy conversions"
        ],
        hint: "It's about Jesus' permanent presence",
        funFact: "🤝 Jesus' presence makes the impossible mission possible!"
      }
    }
  }
};

export default chapter4Translations;